import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { grnService, CreateGrnPayload, CreateGrnItemPayload } from '../services/grnService';

// Get GRN by gate entry ID
export const useGrnByGateEntry = (gateEntryId: number | null) => {
  return useQuery({
    queryKey: ['grn', 'by-gate-entry', gateEntryId],
    queryFn: () => grnService.getByGateEntry(gateEntryId!),
    enabled: !!gateEntryId,
    staleTime: 0, // Always fetch fresh data
    refetchOnMount: true,
  });
};

// Create GRN
export const useCreateGrn = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (payload: CreateGrnPayload) => grnService.createGrn(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['grn'] });
      queryClient.invalidateQueries({ queryKey: ['gateEntries'] });
    },
  });
};

// Get GRN by ID
export const useGrn = (id: number | null) => {
  return useQuery({
    queryKey: ['grn', id],
    queryFn: () => grnService.getGrn(id!),
    enabled: !!id,
    staleTime: 0, // Always fetch fresh data
    refetchOnMount: true,
  });
};

// Get GRN items
export const useGrnItems = (grnId: number | null) => {
  return useQuery({
    queryKey: ['grn-items', grnId],
    queryFn: () => grnService.getItems(grnId!),
    enabled: !!grnId,
    staleTime: 0, // Always fetch fresh data
    refetchOnMount: true,
  });
};

// Add GRN item
export const useAddGrnItem = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ grnId, payload }: { grnId: number; payload: CreateGrnItemPayload }) =>
      grnService.addItem(grnId, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['grn-items', variables.grnId] });
      queryClient.invalidateQueries({ queryKey: ['grn', variables.grnId] });
    },
  });
};

// Confirm GRN
export const useConfirmGrn = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ grnId, clientLocation, grnRemarks, vehicleNo, driverName, driverPhone, transporterName, lrNumber, lrDate }: {
      grnId: number; clientLocation?: string; grnRemarks?: string;
      vehicleNo?: string; driverName?: string; driverPhone?: string;
      transporterName?: string; lrNumber?: string; lrDate?: string;
    }) =>
      grnService.confirmGrn(grnId, { clientLocation, grnRemarks, vehicleNo, driverName, driverPhone, transporterName, lrNumber, lrDate }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['grn', variables.grnId] });
      queryClient.invalidateQueries({ queryKey: ['grn-items', variables.grnId] });
      queryClient.invalidateQueries({ queryKey: ['gateEntries'] });
    },
  });
};

// Update GRN item
export const useUpdateGrnItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ grnId, itemId, payload }: { grnId: number; itemId: number; payload: Partial<CreateGrnItemPayload> }) =>
      grnService.updateItem(grnId, itemId, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['grn-items', variables.grnId] });
      queryClient.invalidateQueries({ queryKey: ['grn', variables.grnId] });
    },
  });
};

// Delete GRN item
export const useDeleteGrnItem = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ grnId, itemId }: { grnId: number; itemId: number }) =>
      grnService.deleteItem(grnId, itemId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['grn-items', variables.grnId] });
      queryClient.invalidateQueries({ queryKey: ['grn', variables.grnId] });
    },
  });
};

// Get GRN list
export const useGrnList = (params?: { page?: number; limit?: number; search?: string }) => {
  return useQuery({
    queryKey: ['grn-list', params],
    queryFn: () => grnService.getGrnList(params),
    staleTime: 0, // Always fetch fresh data
    refetchOnMount: true,
  });
};
